/**
 * Лист наряда с правкой — ОДИН на оба места, где наряд правят:
 * окно сводного отчёта в «Заказах» и сохранённый отчёт в «Документах».
 *
 * Прежде сохранённый наряд показывался отдельной вёрсткой и только для
 * чтения: заметил опечатку в фамилии после сохранения — формируй отчёт
 * заново. Две вёрстки одного листа к тому же расходились при каждой правке:
 * в одной появлялись даты и элементы, в другой — нет.
 *
 * Компонент ничего не хранит сам. Строки и шапка приходят готовыми, а правки
 * уходят наружу: окно в «Заказах» записывает их поверх заказов, окно в
 * «Документах» — в черновик сохранённого снимка.
 */

import { Fragment, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Pencil, X } from 'lucide-react';
import { CORRECTION_ITEM_NAME, formatKop, reportDate, rublesToKop, type Facsimile, type ReportItem } from '@otc/shared';
import { FitToWidth } from './FitToWidth.js';

/** Строка наряда — один заказ. */
export interface SheetRow {
  /** Ключ строки: заказ в «Заказах», позиция снимка в «Документах». */
  key: string;
  patient: string;
  construction: string;
  /** Даты строкой `YYYY-MM-DD`; пустой срок сдачи — «не задан». */
  received: string;
  due: string;
  costKop: number;
  /** Элементы работы — используются только в развёрнутом наряде. */
  items: ReportItem[];
}

/** Строки одного врача. */
export interface SheetGroup {
  key: string;
  name: string;
  rows: SheetRow[];
  subtotalKop: number;
}

export type SheetRowPatch = Partial<Pick<SheetRow, 'patient' | 'construction' | 'received' | 'due' | 'costKop'>>;

export interface SheetHeaderPatch {
  clinicName?: string;
  legalName?: string;
  dateIso?: string;
  responsible?: string;
}

export interface SheetColumns {
  showReceived: boolean;
  showDue: boolean;
  showSubtotal: boolean;
  detailed: boolean;
  /** Подпись и печать исполнителя в рамке «Подпись:». */
  facsimile: boolean;
}

/** Кнопка «Редактировать / Готово» с пояснением рядом. */
export function ReportEditToggle({
  editMode,
  onToggle,
  narrow,
  note,
}: {
  editMode: boolean;
  onToggle: () => void;
  narrow: boolean;
  note: string;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium"
        style={{
          background: editMode ? 'var(--otc-accent)' : 'var(--otc-surface)',
          color: editMode ? '#fff' : 'var(--otc-ink2)',
          borderColor: editMode ? 'var(--otc-accent)' : 'var(--otc-line)',
        }}
      >
        <Pencil size={14} /> {editMode ? 'Готово' : 'Редактировать'}
      </button>

      {editMode && (
        <span className="text-xs text-ink2">
          {note}
          {narrow && ' · лист листается вбок'}
        </span>
      )}
    </div>
  );
}

/** Галочки колонок и вида наряда. */
export function ReportColumnsControls({
  columns,
  onChange,
  facsimileAvailable,
}: {
  columns: SheetColumns;
  onChange: (patch: Partial<SheetColumns>) => void;
  /** Загружены ли на сервер подпись и печать. */
  facsimileAvailable: boolean;
}) {
  /*
   * НА ТЕЛЕФОНЕ — столбиком, на широком экране — строкой.
   *
   * Свободный перенос рвал список как попало: «Колонки:» оставалось наверху
   * одно, «Дата сдачи» и «Подытог по врачу» слипались в одну строку, а
   * остальные шли по одной. Читать такое нельзя — глаз не находит, где
   * кончается одна галочка и начинается другая.
   */
  return (
    <div className="mb-3 grid gap-2 text-xs text-ink2 md:flex md:flex-wrap md:items-center md:gap-4">
      <span className="font-medium md:font-normal">Колонки:</span>
      <label className="flex items-center gap-1.5">
        <input
          type="checkbox"
          checked={columns.showReceived}
          onChange={(e) => onChange({ showReceived: e.target.checked })}
        />
        Дата получения
      </label>
      <label className="flex items-center gap-1.5">
        <input type="checkbox" checked={columns.showDue} onChange={(e) => onChange({ showDue: e.target.checked })} />
        Дата сдачи
      </label>
      <label className="flex items-center gap-1.5">
        <input
          type="checkbox"
          checked={columns.showSubtotal}
          onChange={(e) => onChange({ showSubtotal: e.target.checked })}
        />
        Подытог по врачу
      </label>
      {/* Развёрнутый наряд — такой же галочкой, как колонки рядом. */}
      <label className="flex items-center gap-1.5">
        <input type="checkbox" checked={columns.detailed} onChange={(e) => onChange({ detailed: e.target.checked })} />
        Расписать работу по элементам
      </label>
      <label
        className="flex items-center gap-1.5"
        title={facsimileAvailable ? undefined : 'Файлы подписи и печати не загружены на сервер'}
      >
        <input
          type="checkbox"
          checked={columns.facsimile}
          disabled={!facsimileAvailable && !columns.facsimile}
          onChange={(e) => onChange({ facsimile: e.target.checked })}
        />
        Подпись и печать
      </label>
    </div>
  );
}

export function ReportSheet({
  editMode,
  narrow,
  clinicName,
  legalName,
  dateIso,
  responsible,
  onHeader,
  columns,
  groups,
  totalKop,
  onRow,
  onItems,
  onTotalChange,
  onTotalReset,
  emptyText,
  facsimileImages = null,
}: {
  editMode: boolean;
  /** Телефон: там лист в правке не ужимается, а листается вбок. */
  narrow: boolean;
  clinicName: string;
  legalName: string;
  dateIso: string;
  responsible: string;
  onHeader: (patch: SheetHeaderPatch) => void;
  columns: SheetColumns;
  groups: SheetGroup[];
  totalKop: number;
  onRow: (key: string, patch: SheetRowPatch) => void;
  onItems: (key: string, items: ReportItem[]) => void;
  /** Ручная правка общего итога — только там, где она предусмотрена. */
  onTotalChange?: (kop: number) => void;
  /** Возврат к автоматическому итогу; кнопка видна, только если передан. */
  onTotalReset?: () => void;
  emptyText: string;
  /** Подпись и печать — показываются, если включена галочка «Подпись и печать». */
  facsimileImages?: Facsimile | null;
}) {
  const { showReceived, showDue, showSubtotal, detailed } = columns;
  const dateCols = (showReceived ? 1 : 0) + (showDue ? 1 : 0);
  // В развёрнутом наряде перед суммой не три колонки, а пять: элемент, кол-во, цена.
  const labelColSpan = (detailed ? 5 : 3) + dateCols;

  const editable = 'w-full rounded border px-1 py-0.5 text-xs';
  const editableStyle = { borderColor: 'var(--otc-line)', color: 'var(--otc-ink)' };

  /**
   * Ячейка наряда: сплошная рамка и текст по центру — включая колонку цены.
   * В бланке лаборатории выключки по правому краю нет нигде.
   */
  const cellClass = 'border border-black px-2 py-1 text-center font-normal align-middle text-ink';

  const patchItem = (row: SheetRow, index: number, patch: Partial<ReportItem>) =>
    onItems(row.key, row.items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const removeItem = (row: SheetRow, index: number) =>
    onItems(row.key, row.items.filter((_, i) => i !== index));
  const addItem = (row: SheetRow) => onItems(row.key, [...row.items, { name: '', qty: 1, priceKop: 0 }]);

  /**
   * Ячейка даты заказа: в режиме правки — выбор даты, иначе дата как в бланке.
   * Срок сдачи можно очистить: в наряде вместо даты встанет прочерк.
   */
  const dateCell = (row: SheetRow, field: 'received' | 'due') => {
    if (editMode) {
      return (
        <input
          type="date"
          className={`${editable} tabular text-center`}
          style={editableStyle}
          value={row[field]}
          onChange={(e) => onRow(row.key, { [field]: e.target.value })}
        />
      );
    }
    return field === 'due' ? reportDate(row.due) || '—' : reportDate(row.received);
  };

  const patientCell = (row: SheetRow): ReactNode =>
    editMode ? (
      <input
        className={`${editable} text-center`}
        style={editableStyle}
        value={row.patient}
        onChange={(e) => onRow(row.key, { patient: e.target.value })}
      />
    ) : (
      row.patient
    );

  const subtotalRow = (group: SheetGroup) =>
    showSubtotal && (
      <tr>
        <td colSpan={labelColSpan} className={`${cellClass} italic text-ink2`}>
          Итого {group.name}:
        </td>
        <td className={`${cellClass} tabular italic text-ink2`}>{formatKop(group.subtotalKop)}</td>
      </tr>
    );

  return (
    /*
      Лист ужимается под ширину экрана — для обзора. На ТЕЛЕФОНЕ в правке он
      не ужимается, а листается вбок: ужатый до трети, он давал поля ввода с
      текстом в три пикселя, и браузер при нажатии сам приближал страницу.
      При правке на широком экране лист шире: поля занимают место, которого
      тексту без них хватало.
    */
    <FitToWidth
      naturalWidth={editMode ? (narrow ? 760 : 1150) : 820}
      fit={!(editMode && narrow)}
      className={`mb-4 rounded-lg border bg-white p-3 md:p-6${editMode && narrow ? ' overflow-x-auto' : ''}`}
      style={{ borderColor: 'var(--otc-line)' }}
    >
      {editMode ? (
        <input
          className={`${editable} text-lg`}
          style={editableStyle}
          value={clinicName}
          onChange={(e) => onHeader({ clinicName: e.target.value })}
        />
      ) : (
        <p className="text-lg text-ink">{clinicName}</p>
      )}

      <div className="mt-0.5">
        {editMode ? (
          <input
            className={editable}
            style={{ ...editableStyle, color: 'var(--otc-ink2)' }}
            placeholder="Юр. лицо не указано"
            value={legalName}
            onChange={(e) => onHeader({ legalName: e.target.value })}
          />
        ) : (
          <p className="text-xs text-ink2">{legalName || 'Юр. лицо не указано'}</p>
        )}
      </div>

      {/* Дата наряда — та же, что встанет в шапку PDF и Excel. */}
      {editMode ? (
        <input
          type="date"
          className="tabular mt-2 rounded border px-2 py-1 text-lg"
          style={editableStyle}
          value={dateIso}
          onChange={(e) => onHeader({ dateIso: e.target.value })}
        />
      ) : (
        <p className="tabular mt-2 text-lg text-ink">{reportDate(dateIso)}</p>
      )}
      <p className="mb-4 mt-5 text-center text-xl text-ink">Ортодонтические наряды зубного техника</p>

      {groups.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink2">{emptyText}</p>
      ) : detailed ? (
        /*
          Развёрнутый наряд. На заказ — строки элементов и «Итого по заказу»;
          врач, пациент и даты объединены на весь заказ, как в PDF и Excel.
        */
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th className={cellClass}>ФИО врача</th>
              <th className={cellClass}>ФИО пациента</th>
              <th className={cellClass}>Элемент</th>
              <th className={`${cellClass} w-14`}>Кол-во</th>
              <th className={`${cellClass} w-24`}>Цена</th>
              {showReceived && <th className={cellClass}>Получен</th>}
              {showDue && <th className={cellClass}>Дата сдачи</th>}
              <th className={cellClass}>Сумма</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.key}>
                {group.rows.map((row) => {
                  const span = row.items.length + 1;
                  return (
                    <Fragment key={row.key}>
                      {row.items.map((item, index) => (
                        <tr key={`${row.key}-${index}`}>
                          {index === 0 && (
                            <>
                              <td rowSpan={span} className={cellClass}>{group.name}</td>
                              <td rowSpan={span} className={cellClass}>{patientCell(row)}</td>
                            </>
                          )}

                          <td className={`${cellClass}${item.name === CORRECTION_ITEM_NAME ? ' italic' : ''}`}>
                            {editMode ? (
                              <span className="flex items-center gap-1">
                                <WrappingField
                                  className={`${editable} text-center`}
                                  style={editableStyle}
                                  placeholder="Название элемента"
                                  value={item.name}
                                  onChange={(value) => patchItem(row, index, { name: value })}
                                />
                                {/* Последнюю строку не убрать: заказ без расшифровки теряет смысл. */}
                                {row.items.length > 1 && (
                                  <button
                                    onClick={() => removeItem(row, index)}
                                    className="flex-shrink-0 text-ink2 hover:text-[color:var(--otc-coral)]"
                                    title="Убрать строку"
                                  >
                                    <X size={12} />
                                  </button>
                                )}
                              </span>
                            ) : (
                              item.name
                            )}
                          </td>

                          <td className={cellClass}>
                            {editMode ? (
                              <input
                                className={`${editable} tabular w-12 text-center`}
                                style={editableStyle}
                                inputMode="numeric"
                                value={item.qty}
                                onChange={(e) =>
                                  patchItem(row, index, { qty: Math.max(0, Math.round(Number(e.target.value) || 0)) })
                                }
                              />
                            ) : (
                              <span className="tabular">{item.qty}</span>
                            )}
                          </td>

                          <td className={cellClass}>
                            {editMode ? (
                              <input
                                className={`${editable} tabular w-20 text-center`}
                                style={editableStyle}
                                value={formatKop(item.priceKop)}
                                onChange={(e) => patchItem(row, index, { priceKop: rublesToKop(e.target.value) })}
                              />
                            ) : (
                              <span className="tabular">{formatKop(item.priceKop)}</span>
                            )}
                          </td>

                          {index === 0 && showReceived && (
                            <td rowSpan={span} className={cellClass}>{dateCell(row, 'received')}</td>
                          )}
                          {index === 0 && showDue && (
                            <td rowSpan={span} className={cellClass}>{dateCell(row, 'due')}</td>
                          )}

                          <td className={`${cellClass} tabular`}>{formatKop(item.qty * item.priceKop)}</td>
                        </tr>
                      ))}

                      <tr>
                        <td colSpan={3} className={`${cellClass} text-right font-semibold`} style={{ background: '#EDEBE6' }}>
                          {editMode && (
                            <button
                              onClick={() => addItem(row)}
                              className="float-left text-[10px] font-normal underline"
                              style={{ color: 'var(--otc-accent)' }}
                            >
                              + строка
                            </button>
                          )}
                          Итого по заказу:
                        </td>
                        <td className={`${cellClass} tabular font-semibold`} style={{ background: '#EDEBE6' }}>
                          {formatKop(row.costKop)}
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
                {subtotalRow(group)}
              </Fragment>
            ))}

            <tr>
              <td colSpan={labelColSpan - 1} />
              <td className={cellClass}>Итого:</td>
              <td className={`${cellClass} tabular`}>{formatKop(totalKop)}</td>
            </tr>
          </tbody>
        </table>
      ) : (
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th className={cellClass}>ФИО врача</th>
              <th className={cellClass}>ФИО пациента</th>
              <th className={cellClass}>Конструкция</th>
              {showReceived && <th className={cellClass}>Получен</th>}
              {showDue && <th className={cellClass}>Дата сдачи</th>}
              <th className={cellClass}>Цена</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.key}>
                {group.rows.map((row) => (
                  <tr key={row.key}>
                    {/*
                      Фамилия врача повторяется в КАЖДОЙ строке: так в бланке
                      лаборатории, и строка остаётся понятной, если наряд
                      разрезали по страницам при печати.
                    */}
                    <td className={cellClass}>{group.name}</td>
                    <td className={cellClass}>{patientCell(row)}</td>

                    <td className={cellClass}>
                      {editMode ? (
                        <WrappingField
                          className={`${editable} text-center`}
                          style={editableStyle}
                          value={row.construction}
                          onChange={(value) => onRow(row.key, { construction: value })}
                        />
                      ) : (
                        row.construction
                      )}
                    </td>

                    {showReceived && <td className={cellClass}>{dateCell(row, 'received')}</td>}
                    {showDue && <td className={cellClass}>{dateCell(row, 'due')}</td>}

                    <td className={cellClass}>
                      {editMode ? (
                        <input
                          className={`${editable} tabular w-20 text-center`}
                          style={editableStyle}
                          value={formatKop(row.costKop)}
                          onChange={(e) => onRow(row.key, { costKop: rublesToKop(e.target.value) })}
                        />
                      ) : (
                        <span className="tabular">{formatKop(row.costKop)}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {subtotalRow(group)}
              </Fragment>
            ))}

            {/*
              Итог — короткая строка в двух последних колонках. Слева от неё
              ячеек нет вовсе: в бланке «Итого:» стоит под датой сдачи.
            */}
            <tr>
              <td colSpan={labelColSpan - 1} />
              <td className={cellClass}>Итого:</td>
              <td className={cellClass}>
                <span className="inline-flex items-center gap-1.5">
                  {editMode && onTotalChange ? (
                    <input
                      className={`${editable} tabular w-24 text-center`}
                      style={editableStyle}
                      value={formatKop(totalKop)}
                      onChange={(e) => onTotalChange(rublesToKop(e.target.value))}
                    />
                  ) : (
                    <span className="tabular">{formatKop(totalKop)}</span>
                  )}
                  {editMode && onTotalReset && (
                    <button
                      onClick={onTotalReset}
                      className="text-[10px] underline text-ink2"
                      title="Вернуть автоматический подсчёт"
                    >
                      авто
                    </button>
                  )}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Подвал: обе строки в рамке, как в бланке. */}
      <div className="mt-8 space-y-2">
        <div
          className="inline-flex items-center gap-2 border px-3 py-2 text-base text-ink"
          style={{ borderColor: 'var(--otc-ink)' }}
        >
          <span>Ответственный ФИО :</span>
          {editMode ? (
            <input
              className="rounded border px-1.5 py-0.5 text-sm"
              style={editableStyle}
              value={responsible}
              onChange={(e) => onHeader({ responsible: e.target.value })}
            />
          ) : (
            <span>{responsible || '—'}</span>
          )}
        </div>
        <div
          className="relative border px-3 py-4 text-base text-ink"
          // Под рамкой — место под нижний край печати, иначе лист её обрезает.
          style={{
            borderColor: 'var(--otc-ink)',
            maxWidth: '20rem',
            marginBottom: columns.facsimile && facsimileImages ? 72 : 0,
          }}
        >
          Подпись:
          {columns.facsimile && facsimileImages && (
            <>
              {/* Та же раскладка, что в PDF: подпись в рамке, печать на её краю. */}
              <img src={facsimileImages.signature} alt="" className="pointer-events-none absolute" style={{ left: 110, top: -12, width: 150 }} />
              <img
                src={facsimileImages.stamp}
                alt=""
                className="pointer-events-none absolute"
                style={{ left: 250, top: -6, width: 128, mixBlendMode: 'multiply' }}
              />
            </>
          )}
        </div>
      </div>
    </FitToWidth>
  );
}

/**
 * Поле названия, которое ПЕРЕНОСИТ текст и растёт по высоте.
 *
 * Однострочное поле обрезало длинные названия при любой ширине окна: «Аппарат
 * Haas M.Rosa 3D с винтом Hyrax на верх.чел. + Печать 3D модели + Крючки ×2»
 * не помещается ни в какую колонку. Здесь текст виден целиком.
 *
 * Высота подгоняется после отрисовки, до показа кадра: сначала сбрасывается,
 * потом берётся по содержимому — иначе поле, из которого стёрли строку, так и
 * осталось бы высоким.
 */
function WrappingField({
  value,
  onChange,
  className,
  style,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const field = ref.current;
    if (!field) return;
    field.style.height = 'auto';
    // К высоте содержимого прибавляется рамка: `scrollHeight` её не включает.
    const рамка = field.offsetHeight - field.clientHeight;
    field.style.height = `${field.scrollHeight + рамка}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      className={`${className ?? ''} block resize-none overflow-hidden leading-snug`}
      style={style}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
